import { createSlice } from "@reduxjs/toolkit";
import { getWebhookAPI, updateWebhookAPI, getWebhookTriggersAPI } from "./apis/webhooks";
import { normalizeWebhook } from "@utils/webhook-adapters";
import extractApiError from "@utils/extract-api-error";

const initialState = {
    detail: { pending: false, error: null, data: null },
    triggers: { pending: false, error: null, data: null },
};

const webhookSlice = createSlice({
    name: "webhook",
    initialState,
    reducers: {
        resetDetail(state) {
            state.detail = { pending: false, error: null, data: null };
        },
        detailPending(state) {
            state.detail = { pending: true, error: null, data: state.detail.data };
        },
        detailSuccess(state, action) {
            state.detail = { pending: false, error: null, data: action.payload };
        },
        detailFailure(state, action) {
            state.detail = { pending: false, error: extractApiError(action.payload), data: state.detail.data };
        },
        triggersPending(state) {
            state.triggers = { pending: true, error: null, data: null };
        },
        triggersSuccess(state, action) {
            state.triggers = { pending: false, error: null, data: action.payload };
        },
        triggersFailure(state, action) {
            state.triggers = { pending: false, error: extractApiError(action.payload), data: null };
        },
    },
});

const { detailPending, detailSuccess, detailFailure, triggersPending, triggersSuccess, triggersFailure } =
    webhookSlice.actions;
export const { resetDetail } = webhookSlice.actions;

/**
 * 웹훅 상세 조회.
 * - 재조회 중에는 이전 data 를 유지한다(항목 저장 후 재조회 시 화면이 깜빡이지 않도록).
 */
export const getWebhookDetail = (webhookId) => (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch(detailPending());
    return getWebhookAPI(stage.id, stage.endpoint, project.id, webhookId)
        .then((response) => {
            dispatch(detailSuccess(normalizeWebhook(response.data)));
            return response;
        })
        .catch((error) => {
            dispatch(detailFailure(error));
            throw error;
        });
};

/**
 * 웹훅 수정(부분 수정) 후 상세 재조회.
 * - PATCH 는 204 무본문이라 updated.at/by 를 응답에서 받을 수 없다 → 성공 후 상세를 다시 읽는다.
 * - body 는 변경된 필드만. endpoint/headers 는 configuration 아래로 감싸서 전달할 것.
 * @param {string} webhookId
 * @param {object} body - 예) { name } · { status } · { triggers } · { configuration: { endpoint } }
 */
export const updateWebhook = (webhookId, body) => async (dispatch, getState) => {
    const { stage, project } = getState();
    await updateWebhookAPI(stage.id, stage.endpoint, project.id, webhookId, body);
    return dispatch(getWebhookDetail(webhookId));
};

/**
 * 트리거 카탈로그 조회.
 * - 이미 받아둔 카탈로그가 있으면 재요청하지 않는다.
 */
export const getWebhookTriggers = () => (dispatch, getState) => {
    const { stage, project, webhook } = getState();
    if (webhook.triggers.data || webhook.triggers.pending) return Promise.resolve(webhook.triggers.data);

    dispatch(triggersPending());
    return getWebhookTriggersAPI(stage.id, stage.endpoint, project.id)
        .then((response) => {
            const results = response.data?.results ?? [];
            dispatch(triggersSuccess(results));
            return results;
        })
        .catch((error) => {
            dispatch(triggersFailure(error));
            return null;
        });
};

export default webhookSlice.reducer;
